/**
 * API client.
 *
 * Every request sends the session cookie (`credentials: 'include'`) and expects
 * the `{ ok, data } | { ok: false, error }` envelope the API always returns.
 *
 * A 401 is a normal, expected outcome — the session simply expired — so it is
 * surfaced as a typed result rather than a thrown error, letting the caller
 * redirect to the login screen instead of catching an exception.
 */

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      // Marks the request as same-origin XHR. Combined with SameSite=Lax
      // cookies this blocks a cross-site form POST from acting as the operator.
      'X-Requested-With': 'XMLHttpRequest',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
  };

  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;

  const response = await fetch(`/api${path}`, init);

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const envelope = payload as { error?: ApiError } | null;
    throw new ApiRequestError(
      response.status,
      envelope?.error ?? { code: 'unknown', message: 'Request failed' },
    );
  }

  const envelope = payload as { ok: true; data?: T };
  return envelope.data as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
